import { useAuth0 } from '@auth0/auth0-react';
import { useToastStore } from '../../app/store/useToastStore';
import { useTranslation } from './useTranslation';
import { setReAuthFlag, clearReAuthFlag } from '../utils/reAuthFlag';

// Storage key for re-authentication state (shared with AccountSecuritySettings)
export const REAUTH_STORAGE_KEY = 'account_security_reauth';

export interface ReAuthResult {
  success: boolean;
  token?: string;
  cancelled?: boolean;
}

export const useReAuth = () => {
  const { loginWithPopup, logout, getIdTokenClaims, getAccessTokenSilently, user } = useAuth0();
  const { showToast } = useToastStore();
  const { t } = useTranslation();

  const reAuthenticate = async (): Promise<ReAuthResult> => {
    // Capture email at the very start before any async operations
    const originalEmail = user?.email;
    
    if (!originalEmail) {
      showToast(
        t('auth.reauth.noEmail') || 'Email not found. Please log in again.',
        'error',
        3000
      );
      return { success: false };
    }

    try {
      // Set re-auth flag BEFORE the iframe logout so that AuthInitializer
      // won't wipe user state when Auth0 isAuthenticated briefly flips to false
      setReAuthFlag();

      // First, clear the Auth0 session without logging out of your app
      const logoutUrl = `https://${import.meta.env.VITE_AUTH0_DOMAIN}/v2/logout?client_id=${import.meta.env.VITE_AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}&federated=true`;

      // Open logout in hidden iframe to clear session
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = logoutUrl;
      document.body.appendChild(iframe);

      // Wait for logout to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      document.body.removeChild(iframe);

      // Now force fresh login
      try {
        await loginWithPopup({
          authorizationParams: {
            prompt: 'login',
            max_age: 0,
            login_hint: originalEmail,
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
          timeoutInSeconds: 300,
        });

        // Set re-auth flag immediately after successful login popup
        // This ensures the flag survives any component remounts caused by Auth0 state updates
        setReAuthFlag();
      } catch (popupError: unknown) {
        const errorStr = popupError?.error || '';
        const messageStr = popupError?.message || '';
        const descriptionStr = popupError?.error_description || '';

        // Check if popup was closed/cancelled by user
        const isCancelled =
          errorStr === 'popup_closed_by_user' ||
          errorStr === 'PopupCancelledError' ||
          errorStr === 'user_cancelled' ||
          errorStr === 'access_denied' ||
          errorStr === 'login_required' ||
          messageStr.toLowerCase().includes('cancel') ||
          messageStr.toLowerCase().includes('closed') ||
          messageStr.toLowerCase().includes('user closed') ||
          descriptionStr.toLowerCase().includes('cancel') ||
          descriptionStr.toLowerCase().includes('closed') ||
          descriptionStr.toLowerCase().includes('user closed');

        if (isCancelled) {
          // Clear re-auth flag since flow was cancelled
          clearReAuthFlag();
          showToast(
            t('auth.reauth.cancelled') || 'Re-authentication cancelled',
            'error',
            3000
          );
          return { success: false, cancelled: true };
        }

        throw popupError;
      }

      // Verify email matches
      const idTokenClaims = await getIdTokenClaims();
      const authenticatedEmail = idTokenClaims?.email;
      if (!authenticatedEmail) {
        // Clear re-auth flag before logout
        clearReAuthFlag();
        showToast(
          t('auth.reauth.noEmailInToken') || 'Authentication failed. Please try again.',
          'error',
          3000
        );

        setTimeout(() => {
          logout({
            logoutParams: {
              returnTo: window.location.origin,
              federated: true,
            },
          });
        }, 1500);

        return { success: false };
      }

      if (authenticatedEmail !== originalEmail) {
        // Clear re-auth flag before logout
        clearReAuthFlag();
        showToast(
          t('auth.reauth.emailMismatch') ||
          'Email mismatch during re-authentication. Logging out for security.',
          'error',
          3000
        );

        setTimeout(() => {
          logout({
            logoutParams: {
              returnTo: window.location.origin,
              federated: true,
            },
          });
        }, 1500);

        return { success: false };
      }

      // Get fresh access token-
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
        cacheMode: 'off',
      });

      return { success: true, token: accessToken };

    } catch (error: unknown) {
      // Clear re-auth flag before logout
      clearReAuthFlag();

      showToast(
        t('auth.reauth.securityLogout') ||
        'Re-authentication failed. Logging out for security.',
        'error',
        3000
      );

      setTimeout(() => {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
            federated: true,
          },
        });
      }, 1500);

      return { success: false };
    }
  };

  return { reAuthenticate };
};